import { Button } from '@neo/index'
import { useUpdateNodeInternals } from '@xyflow/react'
import { SquareDashedMousePointer, SquareMousePointer, Zap } from 'lucide-react'
import { getAgentTimezone } from 'v2/pages/conversations/utils'
import dayjs from 'v2/utils/dayjs'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { FlowNodeTypes } from '../../types'
import { NodeExecutionHeaderV2 } from '../analysis-v2/header'
import { useFlowRunLogsUI } from '../hooks/use-flow-run-logs-ui'
import { useFlowRunStore } from '../store'
import type { NodeExecutedEvent } from '../types'
import { buildCanvasIdResolver, highlightSingleNodeAndEdge } from '../utils'

interface HeaderProps {
  variant: 'warning' | 'progress' | 'orange' | 'destructive'
  text: string
  className?: string
  node: NodeExecutedEvent
  icon?: React.ReactNode
}

export const Header = ({ text, className = '', icon, node }: HeaderProps) => {
  const { showNewUI } = useFlowRunLogsUI()
  if (showNewUI) {
    return <NodeExecutionHeaderV2 node={node} />
  }

  const { fitView, getNode } = useTypedReactFlow()
  const updateInternals = useUpdateNodeInternals()
  const nodeId = node.current_node_uuid
  const chosenPath = node.chosen_path
  const previousNode = node.previous_node_uuid

  const handleGoToNode = () => {
    if (!nodeId) return

    const toCanvasId = chosenPath
      ? highlightSingleNodeAndEdge(nodeId, chosenPath, previousNode)
      : buildCanvasIdResolver()
    const canvasNodeId = toCanvasId(nodeId)
    if (!getNode(canvasNodeId)) return

    fitView({
      nodes: [{ id: canvasNodeId }],
      duration: 300,
      maxZoom: 0.9,
    })

    if (chosenPath) updateInternals(canvasNodeId)
  }

  const agentTimezone = getAgentTimezone()
  const formattedTime = dayjs.utc(node.timestamp).tz(agentTimezone).format('DD MMM, HH:mm:ss')

  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 ${className}`}>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-2 text-h4-500 text-primary">
          {icon || <SquareMousePointer className="size-4" />}
          <span>{text}</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-jetbrains text-xs-400 text-secondary tabular-nums whitespace-nowrap">
          {formattedTime}
        </span>
        {nodeId && (
          <Button
            variant="ghost"
            size="iconXs"
            tooltipContent="Go to node"
            aria-label="Go to node"
            onClick={handleGoToNode}
            data-testid="button-go-to-node"
          >
            <SquareDashedMousePointer className="size-4" />
          </Button>
        )}
      </div>
    </div>
  )
}

// Convenience components for specific use cases
export const AIResponseHeader = ({
  chunkTimestamp,
  node,
}: {
  chunkTimestamp?: string
  node: NodeExecutedEvent
}) => {
  const executionHistory = useFlowRunStore((state) => state.executionHistory)

  // Calculate response number by counting assistant chunks up to this timestamp
  let responseNumber = 0

  executionHistory.some((event) => {
    if (!event.chunks) return false

    return event.chunks.some((chunk) => {
      if (chunk.sender === 'assistant' && chunk.event_name === 'NODE_CHUNK_CREATED') {
        responseNumber += 1
        if (chunk.timestamp === chunkTimestamp) {
          return true
        }
      }
      return false
    })
  })

  return <Header variant="warning" text={`AI Response ${responseNumber}`} node={node} />
}

export const NodeExecutionHeader = ({ node }: { node: NodeExecutedEvent }) => {
  const presetMessageNodesTypes = [
    FlowNodeTypes.ai_agent_whatsapp,
    FlowNodeTypes.ai_agent_chat,
    FlowNodeTypes.agent_preset_conversation,
  ]
  if (node.current_node_type && presetMessageNodesTypes.includes(node.current_node_type))
    return <Header variant="warning" text="Send message" className="mb-2" node={node} />

  return <Header variant="progress" text="Node execution" className="mb-2" node={node} />
}

export const ToolCallHeader = ({ node }: { node: NodeExecutedEvent }) => (
  <Header variant="orange" text="Tool execution" node={node} icon={<Zap className="w-4 h-4" />} />
)

export const FlowCompletionHeader = ({ node }: { node: NodeExecutedEvent }) => (
  <Header variant="destructive" text="Test End" className="mb-2" node={node} />
)
